<template>
  <div id="app">
    <div v-if="show && (company || !hasId)">
      <NuxtPage />
    </div>
  </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n'
import { useRequestHeaders, useRoute, useRuntimeConfig } from '#app'
import i18n, { setLocale } from './utils/i18n'
import { useCompanyStore } from './store/company'
import {
  setAcceptLanguage,
  setBaseUrl,
  setCookies,
  setXsrfToken
} from './utils/api'
import { useVacancyStore } from '~/store/vacancy'
import { CompanyFetchStatus, VacancyFetchStatus } from '~/utils/enums'
import { useRedactorStore } from '~/store/redactor'
import { ref } from '#imports'

export default {
  name: 'App',
  async setup () {
    const route = useRoute()
    const headers = useRequestHeaders(['cookie'])
    const companyStore = useCompanyStore()
    const vacancyStore = useVacancyStore()
    const id = route.params.id
      ? Number.parseInt(route.params.id.toString())
      : null
    const config = useRuntimeConfig()

    let domain = ''

    if (process.client) {
      domain = window.location.host
    } else {
      domain = headers.host!
    }

    companyStore.setIsDomain(!!domain && !id)

    if (!id && !domain && process.client) {
      window.location.href = config.public.frontUrl
    }

    const templateName = route?.params?.template_name as string
    const show = ref(false)
    const redactorStore = useRedactorStore()

    const { locale: currentLocale } = useI18n()
    const locale = useCookie('locale')
    setCookies(headers.cookie)

    const token = useCookie('XSRF-TOKEN', { watch: true })
    if (token.value) {
      setXsrfToken(token.value)
    }

    setBaseUrl(config.public.backUrl)

    if (locale.value) {
      i18n.global.locale.value = locale.value
    }
    setAcceptLanguage(i18n.global.locale.value)

    companyStore.$subscribe((_, { company }) => {
      if (!company) {
        return
      }

      locale.value = company.country.locale
      i18n.global.locale.value = locale.value!
      setLocale(locale.value!)
      currentLocale.value = locale.value!
    })

    if (route.path.includes('preview')) {
      companyStore.setPreview()
    } else if (route.path.includes('redacting')) {
      companyStore.setRedacting()
    }

    if (templateName) {
      companyStore.setTemplateName(templateName)
    }

    function redirectToFront (frontUrl: string, id: number) {
      if (process.client) {
        window.location.href = `${frontUrl}company/${id}`
      }
      show.value = false
    }

    if ((companyStore.preview || companyStore.redacting) && !!templateName) {
      companyStore.fetchRedactingData()
    }

    async function setPreviewMode () {
      await companyStore.fetchPreviewData()
      show.value = true
    }

    async function setRedactingMode () {
      await companyStore.fetchRedactingData()
      show.value = true
    }

    function setDefaultMode () {
      show.value = true
    }

    function checkIfAuthFetchFailed (fetched, fetchedVacancies) {
      return (
        fetched === CompanyFetchStatus.FAILURE ||
        fetchedVacancies === VacancyFetchStatus.FAILURE
      )
    }

    function checkIfPreviewMode (fetched) {
      return fetched === CompanyFetchStatus.AUTH && companyStore.preview
    }

    function checkIfRedactingMode (fetched) {
      return fetched === CompanyFetchStatus.AUTH && companyStore.redacting
    }

    function checkIfHasNoTemplate () {
      return !companyStore.company.companyTemplateSettings
    }

    function checkIfNotAuthAndHaveRestrictedMode (fetched) {
      return (
        fetched !== CompanyFetchStatus.AUTH &&
        (companyStore.redacting || companyStore.preview)
      )
    }

    function checkIdHaveRestrictedMode () {
      return companyStore.redacting || companyStore.preview
    }

    function redirectToMainCareerPortalPage (id) {
      if (process.client) {
        window.location.href = `/${id}`
      }
    }

    function setRedactingData () {
      redactorStore.setRedactorDataIfEmpty(
        companyStore.redactingData?.templateData ||
          companyStore.company?.companyTemplateSettings?.templateData
      )
    }

    async function processIfTokenExists (fetchedVacancies, id?: number) {
      if (!id) {
        return
      }
      const fetched = await companyStore.fetchAuthenticatedOrById(id)
      if (checkIfAuthFetchFailed(fetched, fetchedVacancies)) {
        redirectToFront(config.public.frontUrl, id)
      } else if (checkIfPreviewMode(fetched)) {
        await setPreviewMode()
      } else if (checkIfRedactingMode(fetched)) {
        await setRedactingMode()
      } else if (checkIfHasNoTemplate()) {
        redirectToFront(config.public.frontUrl, id)
      } else if (checkIfNotAuthAndHaveRestrictedMode(fetched)) {
        redirectToMainCareerPortalPage(id)
      } else {
        setDefaultMode()
      }
    }

    async function processIfTokenNotExists (id?: number, domain?: string) {
      if (id) {
        await companyStore.fetchById(id)
      } else if (domain) {
        await companyStore.fetchByDomain(domain.replace('www.', ''))
      }
      if (!companyStore.company?.id) {
        return
      }
      if (checkIdHaveRestrictedMode()) {
        redirectToMainCareerPortalPage(companyStore.company.id)
      } else if (checkIfHasNoTemplate()) {
        redirectToFront(config.public.frontUrl, companyStore.company.id)
      } else {
        setDefaultMode()
      }
    }

    if (id) {
      const fetchedVacancies = await vacancyStore.fetchByCompanyId(id)
      await companyStore.fetchArticlesByCompanyId(id)
      if (token.value) {
        await processIfTokenExists(fetchedVacancies, id)
      } else {
        await processIfTokenNotExists(id)
      }
    } else if (domain) {
      await processIfTokenNotExists(undefined, domain)
      if (!companyStore.company?.id) {
        return
      }
      await Promise.all([
        vacancyStore.fetchByCompanyId(companyStore.company.id),
        companyStore.fetchArticlesByCompanyId(companyStore.company.id)
      ])
    } else {
      setDefaultMode()
    }

    setRedactingData()

    return {
      company: computed(() => companyStore.company),
      hasId: computed(() => !!id),
      show
    }
  }
}
</script>

<style>
#app {
  overflow: hidden;
}
</style>